import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const API_BASE_URL = 'https://myreadyroom-api-crchhghhctcvgagq.westus2-01.azurewebsites.net';

export default function Login() {
  const navigate = useNavigate();
  const { loginWithRedirect } = useAuth0();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) navigate('/home');
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, { email, password });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      navigate('/home');
    } catch {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <p style={styles.brand}>VIRILITER</p>
        <h1 style={styles.title}>My Ready Room</h1>
        <p style={styles.subtitle}>Executive Interview & Presentation Prep</p>

        <form onSubmit={handleLogin} style={styles.form}>
          {error && <p style={styles.error}>{error}</p>}
          <input
            style={styles.input}
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <input
            style={styles.input}
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
          <button style={styles.button} type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p style={styles.forgotPassword}>
          <Link to="/forgot-password" style={styles.forgotLink}>Forgot password?</Link>
        </p>

        <div style={styles.divider}>
          <div style={styles.dividerLine} />
          <span style={styles.dividerText}>or continue with</span>
          <div style={styles.dividerLine} />
        </div>

        <div style={styles.socialButtons}>
          <button
            style={styles.socialButton}
            onClick={() => loginWithRedirect({ authorizationParams: { connection: 'google-oauth2' } })}
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" style={styles.socialIcon} />
            Google
          </button>
          <button
            style={styles.socialButton}
            onClick={() => loginWithRedirect({ authorizationParams: { connection: 'facebook' } })}
          >
            <img src="https://facebook.com/favicon.ico" alt="Facebook" style={styles.socialIcon} />
            Facebook
          </button>
        </div>

        <button
          style={styles.appleButton}
          onClick={() => loginWithRedirect({ authorizationParams: { connection: 'apple' } })}
        >
          <svg width="16" height="16" viewBox="0 0 384 512" fill="#ffffff" style={styles.appleIcon}>
            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
          </svg>
          <span>Sign in with Apple</span>
        </button>

        <div style={styles.bottomLinks}>
          <p style={styles.link}>
            Don't have an account?{' '}
            <Link to="/register" style={styles.linkAccent}>Register</Link>
          </p>
        </div>

        <p style={styles.footer}>Train for the moment that defines your career.</p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', backgroundColor: '#0A0F1E', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
  card: { width: '100%', maxWidth: '420px', display: 'flex', flexDirection: 'column', gap: '16px' },
  brand: { color: '#3B82F6', fontSize: '11px', letterSpacing: '6px', fontWeight: '700', textAlign: 'center', margin: 0 },
  title: { color: '#F1F5F9', fontSize: '34px', fontWeight: '800', textAlign: 'center', margin: 0, letterSpacing: '-0.5px' },
  subtitle: { color: '#475569', fontSize: '13px', textAlign: 'center', margin: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: '12px' },
  error: { color: '#EF4444', fontSize: '14px', margin: 0 },
  input: { backgroundColor: '#0F172A', color: '#F1F5F9', padding: '16px', borderRadius: '12px', fontSize: '16px', border: '1px solid #1E293B', outline: 'none', width: '100%', boxSizing: 'border-box' },
  button: { backgroundColor: '#3B82F6', color: '#ffffff', padding: '18px', borderRadius: '12px', fontSize: '16px', fontWeight: '700', border: 'none', cursor: 'pointer', width: '100%' },
  forgotPassword: { textAlign: 'right', margin: '0' },
  forgotLink: { color: '#475569', fontSize: '13px', textDecoration: 'none' },
  divider: { display: 'flex', alignItems: 'center', gap: '12px' },
  dividerLine: { flex: 1, height: '1px', backgroundColor: '#1E293B' },
  dividerText: { color: '#475569', fontSize: '12px', whiteSpace: 'nowrap' },
  socialButtons: { display: 'flex', gap: '12px' },
  socialButton: { flex: 1, backgroundColor: '#0F172A', color: '#F1F5F9', padding: '14px', borderRadius: '12px', fontSize: '14px', fontWeight: '600', border: '1px solid #1E293B', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  socialIcon: { width: '18px', height: '18px', borderRadius: '2px' },
  appleButton: { backgroundColor: '#000000', color: '#ffffff', padding: '14px', borderRadius: '12px', fontSize: '14px', fontWeight: '600', border: '1px solid #000000', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%' },
  appleIcon: { marginBottom: '2px' },
  bottomLinks: { display: 'flex', justifyContent: 'center' },
  link: { color: '#475569', textAlign: 'center', fontSize: '14px', margin: 0 },
  linkAccent: { color: '#3B82F6', fontWeight: '600', textDecoration: 'none' },
  footer: { color: '#1E293B', textAlign: 'center', fontSize: '12px', margin: 0 },
};